import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { collection, query, where, getDocs, updateDoc, doc, increment, addDoc } from "firebase/firestore";
import { db } from "@/firebase/config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Loader2, AlertCircle, CheckCircle2, Clock, Ban, Users, Link2Off, ShieldX } from "lucide-react";

interface VolunteerSession {
  volunteerName: string;
  schoolID: string;
  linkId: string;
  code: string;
  expiresAt: string;
}

type ErrorType = 'not_found' | 'expired' | 'revoked' | 'max_uses' | 'system' | null;

const VolunteerLogin = () => {
    const { inviteCode } = useParams();
    const navigate = useNavigate();
    
    const [loading, setLoading] = useState(true);
    const [errorType, setErrorType] = useState<ErrorType>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const [linkData, setLinkData] = useState<any | null>(null);
    const [volunteerName, setVolunteerName] = useState("");
    const [inputPasscode, setInputPasscode] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!inviteCode) {
            setErrorType('not_found');
            setLoading(false);
            return;
        }

        const checkLink = async () => {
             try {
                 const q = query(collection(db, "volunteerLinks"), where("code", "==", inviteCode));
                 const snapshot = await getDocs(q);
                 
                 if (snapshot.empty) {
                     setErrorType('not_found');
                     setLoading(false);
                     return;
                 }

                 const docData = snapshot.docs[0].data();
                 const id = snapshot.docs[0].id;
                 const now = new Date();

                 if (docData.status === 'revoked') {
                     setErrorType('revoked');
                 } else if (new Date(docData.expiresAt) < now) {
                     setErrorType('expired');
                 } else if (docData.usedCount >= docData.maxUses) {
                     setErrorType('max_uses');
                 } else {
                     setLinkData({ ...docData, id });
                 }
                 setLoading(false);
             } catch (e) {
                 console.error(e);
                 setErrorType('system');
                 setLoading(false);
             }
        };

        checkLink();
    }, [inviteCode]);

    const handleJoin = async () => {
        setFormError(null);
        if (!volunteerName.trim() || !linkData) return;
        
        // Passcode Check
        if (linkData.passcode && (!inputPasscode || inputPasscode !== linkData.passcode)) {
            setFormError("Incorrect passcode. Please check with the administrator.");
            return;
        }

        setSubmitting(true);

        try {
            // 1. Increment usage on link
            const linkRef = doc(db, "volunteerLinks", linkData.id);
            await updateDoc(linkRef, {
                usedCount: increment(1)
            });

            // 2. Create Volunteer Session in Firestore
            const sessionData = {
                volunteerName: volunteerName.trim(),
                linkId: linkData.id,
                linkName: linkData.name,
                schoolID: linkData.schoolID,
                code: linkData.code,
                status: 'active', // active | paused | revoked
                createdAt: new Date().toISOString(),
                expiresAt: linkData.expiresAt
            };

            const sessionRef = await addDoc(collection(db, "volunteerSessions"), sessionData);

            // 3. Store in localStorage with session ID
            const localSession: VolunteerSession & { sessionId: string } = {
                volunteerName: volunteerName.trim(),
                schoolID: linkData.schoolID,
                linkId: linkData.id,
                code: linkData.code,
                expiresAt: linkData.expiresAt,
                sessionId: sessionRef.id
            };

            localStorage.setItem("vochak_volunteer_session", JSON.stringify(localSession));

            // 4. Redirect
            navigate("/volunteer-dashboard");

        } catch (e) {
            console.error(e);
            setFormError("Failed to join. Please try again.");
            setSubmitting(false);
        }
    };

    // Error page configurations
    const errorConfigs: Record<Exclude<ErrorType, null>, { icon: React.ReactNode; title: string; message: string; color: string }> = {
        not_found: {
            icon: <Link2Off className="h-16 w-16" />,
            title: "Link Not Found",
            message: "This invitation link doesn't exist or may have been deleted. Please check with the administrator who provided you with this link.",
            color: "text-gray-500"
        },
        expired: {
            icon: <Clock className="h-16 w-16" />,
            title: "Link Expired",
            message: "This invitation has expired. Invitation links are only valid for a limited time. Please contact the administrator for a new link.",
            color: "text-amber-500"
        },
        revoked: {
            icon: <Ban className="h-16 w-16" />,
            title: "Access Revoked",
            message: "This invitation has been revoked by an administrator. If you believe this is an error, please contact the school administrator.",
            color: "text-red-500"
        },
        max_uses: {
            icon: <Users className="h-16 w-16" />,
            title: "Link Limit Reached",
            message: "This invitation has reached its maximum number of users. Please contact the administrator to request a new invitation link.",
            color: "text-orange-500"
        },
        system: {
            icon: <ShieldX className="h-16 w-16" />,
            title: "System Error",
            message: "We encountered an error while verifying your invitation. Please try again later or contact support if the problem persists.",
            color: "text-red-500"
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <div className="text-center">
                    <Loader2 className="h-10 w-10 animate-spin text-orange-600 mx-auto" />
                    <p className="mt-4 text-gray-500">Verifying invitation...</p>
                </div>
            </div>
        );
    }

    // Error State - Full Page
    if (errorType) {
        const config = errorConfigs[errorType];
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 p-4">
                <Card className="w-full max-w-lg shadow-xl border-0">
                    <CardContent className="pt-10 pb-8 text-center">
                        <div className={`${config.color} mx-auto mb-6`}>
                            {config.icon}
                        </div>
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                            {config.title}
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto">
                            {config.message}
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                            <Button 
                                variant="outline" 
                                onClick={() => window.location.reload()}
                                className="px-6"
                            >
                                Try Again
                            </Button>
                            <Button 
                                onClick={() => navigate("/")}
                                className="px-6 bg-orange-600 hover:bg-orange-700"
                            >
                                Go to Homepage
                            </Button>
                        </div>
                        <p className="text-xs text-gray-400 mt-8">
                            Error Code: {errorType.toUpperCase()} • Link: {inviteCode || 'N/A'}
                        </p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Valid Link - Show Join Form
    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 p-4">
            <Card className="w-full max-w-md shadow-xl border-t-4 border-t-orange-500">
                <CardHeader>
                    <CardTitle className="text-2xl text-center">Volunteer Access</CardTitle>
                    <CardDescription className="text-center">
                        Join {linkData?.name || "School"} Registration Drive
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="space-y-6">
                        <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg flex items-start gap-3 border border-green-100 dark:border-green-800">
                            <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                            <div>
                                <h4 className="font-semibold text-sm text-green-900 dark:text-green-100">Valid Invitation</h4>
                                <p className="text-xs text-green-700 dark:text-green-300 mt-1">
                                    You are joining <strong>{linkData?.name}</strong>. 
                                    Session expires: {new Date(linkData?.expiresAt).toLocaleDateString()} at {new Date(linkData?.expiresAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}.
                                </p>
                            </div>
                        </div>

                        {/* Passcode Input */}
                        {linkData?.passcode && (
                            <div className="space-y-2">
                                <label className="text-sm font-medium flex justify-between">
                                    Access Passcode <span className="text-xs text-red-500">*Required</span>
                                </label>
                                <Input 
                                    type="text"
                                    placeholder="Enter passcode" 
                                    value={inputPasscode}
                                    onChange={(e) => setInputPasscode(e.target.value)}
                                    className="h-12 font-mono tracking-widest text-center text-lg"
                                    maxLength={10}
                                />
                            </div>
                        )}

                        <div className="space-y-2">
                            <label className="text-sm font-medium">Your Full Name</label>
                            <Input 
                                placeholder="e.g. John Doe" 
                                value={volunteerName}
                                onChange={(e) => setVolunteerName(e.target.value)}
                                className="h-12"
                            />
                            <p className="text-xs text-muted-foreground">This will be recorded on student submissions.</p>
                        </div>

                        {formError && (
                            <div className="flex items-center gap-2 text-sm text-red-600 font-medium bg-red-50 dark:bg-red-900/20 p-3 rounded-lg border border-red-100 dark:border-red-800">
                                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                                {formError}
                            </div>
                        )}

                        <Button 
                            className="w-full h-12 text-base bg-orange-600 hover:bg-orange-700 shadow-lg" 
                            onClick={handleJoin}
                            disabled={!volunteerName.trim() || (linkData?.passcode && !inputPasscode) || submitting}
                        >
                            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                            Start Registration Session
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default VolunteerLogin;

